import { IconStar, IconStarFilled } from "@tabler/icons-react"
import { type FC, useCallback, useEffect, useState } from "react"

import type { LandingPageTestimonialsBlock } from "@forento/core/models/landing"
import { landingPageCopyCharacterLimits } from "@forento/core/schemas/landing"
import { getPlainOrLexicalTextCharacterCount } from "@forento/core/utilities/lexical/index"
import {
	getVimeoIdFromUrl,
	getVimeoUrlFromId,
	getYouTubeIdFromUrl,
	getYouTubeUrlFromId,
} from "@forento/core/utilities/url"
import { VimeoPlayer, YoutubePlayer } from "@forento/ui/components/VideoPlayer"
import { cn } from "@forento/ui/utilities/tailwind"

import Button from "~/components/Button"
import Dropdown from "~/components/Dropdown"
import ImageUploader from "~/components/ImageUploader"
import InputField from "~/components/InputField"
import useFileUpload from "~/hooks/useFileUpload"

import EditableField from "./EditableField"
import EditorShell from "./EditorShell"
import RichEditableField from "./RichEditableField"

type Testimonial = LandingPageTestimonialsBlock["content"]["testimonials"][number]
type ContentType = "quote" | "youtube" | "vimeo"
type EditableTestimonial = Omit<Testimonial, "media"> & { contentType: ContentType; videoUrl: string }

type Props = {
	block: LandingPageTestimonialsBlock
	onChange: (block: LandingPageTestimonialsBlock) => void
	onClose: () => void
}
const TestimonialsBlockEditor: FC<Props> = ({ block, onChange, onClose }) => {
	const [title, setTitle] = useState(block.content.title ?? "")
	const [testimonials, setTestimonials] = useState(() => block.content.testimonials.map(toEditableTestimonial))

	const imageUpload = useFileUpload()

	useEffect(() => {
		onChange({
			type: "testimonials",
			content: {
				title: title.trim() || undefined,
				testimonials: testimonials.map(toTestimonialContent).filter(isCompleteTestimonial),
			},
		})
	}, [onChange, testimonials, title])

	const updateTestimonial = useCallback((index: number, update: Partial<EditableTestimonial>) => {
		setTestimonials(prev =>
			prev.map((testimonial, currentIndex) =>
				currentIndex === index ? { ...testimonial, ...update } : testimonial,
			),
		)
	}, [])

	const handleQuoteChange = useCallback(
		(index: number) => (value: string | null) => {
			updateTestimonial(index, { quote: value ?? "" })
		},
		[updateTestimonial],
	)

	const handleAvatarUpload = async (index: number, file: File) => {
		const uploaded = await imageUpload.upload({ file, kind: "landing-image" })
		if (uploaded === null) return
		setTestimonials(prev =>
			prev.map((testimonial, currentIndex) =>
				currentIndex === index
					? { ...testimonial, avatar: { url: uploaded.publicUrl, alt: testimonial.name } }
					: testimonial,
			),
		)
	}

	return (
		<EditorShell onClose={onClose}>
			<section className="px-6 pt-16 pb-8 sm:pt-20 lg:px-8">
				<div className="mx-auto max-w-7xl">
					<EditableField
						value={title}
						onChange={setTitle}
						placeholder="Section title"
						className="mb-12 text-center"
						maxCharacters={landingPageCopyCharacterLimits.title}
						inputClassName="text-center font-bold text-3xl text-gray-900 tracking-tight sm:text-4xl"
					/>
					<div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
						{testimonials.map((testimonial, index) => {
							const videoId = getVideoId(testimonial)

							return (
								<div
									key={index}
									className="relative flex flex-col rounded-2xl bg-white p-8 shadow-lg ring-1 shadow-gray-900/5 ring-gray-200/50"
								>
									<div className="mb-6 flex items-center justify-between gap-3">
										<div className="text-xs font-medium tracking-[0.18em] text-gray-500 uppercase">
											Testimonial
										</div>
										<button
											type="button"
											onClick={() => setTestimonials(prev => prev.filter((_, i) => i !== index))}
											className="cursor-pointer text-xs text-gray-400 transition-colors hover:text-red-600"
										>
											Remove
										</button>
									</div>
									<div className="mb-6 flex items-start gap-4">
										<AvatarPreview testimonial={testimonial} />
										<div className="min-w-0 flex-1">
											<ImageUploader
												filePath={testimonial.avatar?.url ?? null}
												onUpload={file => handleAvatarUpload(index, file)}
												onDelete={() => updateTestimonial(index, { avatar: undefined })}
											/>
										</div>
									</div>
									<EditableField
										value={testimonial.name}
										onChange={value => updateTestimonial(index, { name: value })}
										placeholder="Name"
										maxCharacters={landingPageCopyCharacterLimits.shortLabel}
										inputClassName="font-bold text-gray-900 text-xl tracking-tight"
									/>
									<div className="mt-2 grid gap-2 sm:grid-cols-2">
										<EditableField
											value={testimonial.role ?? ""}
											onChange={value => updateTestimonial(index, { role: value })}
											placeholder="Role/title"
											maxCharacters={landingPageCopyCharacterLimits.shortLabel}
											showCharacterCounter={false}
											className="rounded-lg bg-gray-50 px-3 py-2"
											inputClassName="text-gray-500 text-sm placeholder:text-gray-400"
										/>
										<EditableField
											value={testimonial.company ?? ""}
											onChange={value => updateTestimonial(index, { company: value })}
											placeholder="Company"
											maxCharacters={landingPageCopyCharacterLimits.shortLabel}
											showCharacterCounter={false}
											className="rounded-lg bg-gray-50 px-3 py-2"
											inputClassName="text-gray-500 text-sm placeholder:text-gray-400"
										/>
									</div>
									<div className="mt-5 rounded-xl border border-black/10 bg-gray-50 p-4">
										<Dropdown
											label="Content"
											items={[
												{ id: "quote", title: "Quote" },
												{ id: "youtube", title: "YouTube" },
												{ id: "vimeo", title: "Vimeo" },
											]}
											selectedItemId={testimonial.contentType}
											onChange={id =>
												updateTestimonial(index, {
													contentType: id as ContentType,
													videoUrl: "",
												})
											}
										/>
										{testimonial.contentType === "quote" ? (
											<RichEditableField
												initialValue={testimonial.quote ?? ""}
												onChange={handleQuoteChange(index)}
												className="mt-4"
												maxCharacters={landingPageCopyCharacterLimits.itemDescription}
											/>
										) : (
											<div className="mt-4">
												<div className="mb-4 overflow-hidden rounded-lg bg-white ring-1 ring-gray-200">
													{testimonial.contentType === "youtube" && videoId ? (
														<YoutubePlayer videoId={videoId} />
													) : testimonial.contentType === "vimeo" && videoId ? (
														<VimeoPlayer videoId={videoId} />
													) : (
														<div className="flex aspect-video w-full items-center justify-center text-sm text-gray-400">
															Video URL
														</div>
													)}
												</div>
												<InputField
													label={
														testimonial.contentType === "youtube"
															? "YouTube URL"
															: "Vimeo URL"
													}
													value={testimonial.videoUrl}
													onChange={value => updateTestimonial(index, { videoUrl: value })}
													placeholder="https://..."
													inputType="url"
												/>
											</div>
										)}
									</div>
									<div className="mt-5 rounded-xl border border-black/10 bg-white p-4">
										<label className="flex cursor-pointer items-center gap-2.5">
											<input
												type="checkbox"
												checked={testimonial.rating !== undefined}
												onChange={event =>
													updateTestimonial(index, {
														rating: event.target.checked ? 5 : undefined,
													})
												}
												className="cursor-pointer rounded border-stone-300 text-stone-700"
											/>
											<span className="text-sm text-stone-600">Show star rating</span>
										</label>
										{testimonial.rating !== undefined && (
											<div className="mt-3 flex items-center gap-1">
												{ratingOptions.map(rating => {
													const Icon =
														rating <= (testimonial.rating ?? 0) ? IconStarFilled : IconStar

													return (
														<button
															key={rating}
															type="button"
															onClick={() => updateTestimonial(index, { rating })}
															aria-label={`${rating} stars`}
															className={cn(
																"cursor-pointer rounded-md p-1 transition-colors hover:bg-amber-50",
																rating <= (testimonial.rating ?? 0)
																	? "text-amber-500"
																	: "text-gray-300",
															)}
														>
															<Icon className="size-5" />
														</button>
													)
												})}
											</div>
										)}
									</div>
								</div>
							)
						})}
					</div>
					<Button
						variant="secondary"
						onClick={() => setTestimonials(prev => [...prev, createEmptyTestimonial()])}
						className="mt-6 cursor-pointer"
					>
						Add testimonial
					</Button>
				</div>
			</section>
		</EditorShell>
	)
}

const AvatarPreview: FC<{ testimonial: EditableTestimonial }> = ({ testimonial }) => {
	if (testimonial.avatar?.url) {
		return (
			<img
				src={testimonial.avatar.url}
				alt={testimonial.avatar.alt ?? testimonial.name}
				className="size-12 shrink-0 rounded-full bg-gray-100 object-cover ring-1 ring-gray-200"
			/>
		)
	}

	return (
		<div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-gray-900 text-sm font-bold text-white">
			{getInitials(testimonial.name)}
		</div>
	)
}

const ratingOptions = [1, 2, 3, 4, 5] as const

function createEmptyTestimonial(): EditableTestimonial {
	return { name: "", quote: "", contentType: "quote", videoUrl: "" }
}

function toEditableTestimonial(testimonial: Testimonial): EditableTestimonial {
	return {
		avatar: testimonial.avatar,
		name: testimonial.name,
		role: testimonial.role,
		company: testimonial.company,
		quote: testimonial.quote ?? "",
		rating: testimonial.rating,
		contentType: testimonial.media?.type ?? "quote",
		videoUrl:
			testimonial.media?.type === "youtube"
				? getYouTubeUrlFromId(testimonial.media.videoId)
				: testimonial.media?.type === "vimeo"
					? getVimeoUrlFromId(testimonial.media.videoId)
					: "",
	}
}

function toTestimonialContent(testimonial: EditableTestimonial): Testimonial {
	const media = getTestimonialMedia(testimonial)
	const quote =
		testimonial.contentType === "quote" && getPlainOrLexicalTextCharacterCount(testimonial.quote ?? "") > 0
			? testimonial.quote?.trim()
			: undefined

	return {
		avatar: testimonial.avatar?.url
			? {
					url: testimonial.avatar.url.trim(),
					alt: testimonial.avatar.alt?.trim() || testimonial.name.trim(),
				}
			: undefined,
		name: testimonial.name.trim(),
		role: testimonial.role?.trim() || undefined,
		company: testimonial.company?.trim() || undefined,
		quote,
		rating: testimonial.rating,
		media,
	}
}

function isCompleteTestimonial(testimonial: Testimonial): boolean {
	return (
		testimonial.name.trim().length > 0 &&
		(testimonial.media !== undefined || getPlainOrLexicalTextCharacterCount(testimonial.quote ?? "") > 0)
	)
}

function getTestimonialMedia(testimonial: EditableTestimonial): Testimonial["media"] {
	const videoId = getVideoId(testimonial)
	if (!videoId) return undefined
	if (testimonial.contentType === "youtube") return { type: "youtube", videoId }
	if (testimonial.contentType === "vimeo") return { type: "vimeo", videoId }
	return undefined
}

function getVideoId(testimonial: EditableTestimonial): string | null {
	if (testimonial.contentType === "youtube") return getYouTubeIdFromUrl(testimonial.videoUrl.trim())
	if (testimonial.contentType === "vimeo") return getVimeoIdFromUrl(testimonial.videoUrl.trim())
	return null
}

function getInitials(name: string): string {
	const words = name.trim().split(/\s+/).filter(Boolean)
	const initials = words
		.slice(0, 2)
		.map(word => word[0])
		.join("")
		.toUpperCase()
	return initials || "?"
}

export default TestimonialsBlockEditor
